/**
 * @fileoverview キャラクターアバターコンポーネント
 * @description 空兵衛と誠之介のアバター表示
 */
import { cn } from '@/lib/utils'

/**
 * キャラクター種別
 */
export type CharacterType = 'sorabei' | 'seinosuke'

/**
 * キャラクター情報
 */
export const CHARACTER_INFO = {
  sorabei: {
    name: '空兵衛（家老）',
    color: '#2E7D32',
    image: '/characters/sorabei.png',
    role: '家老',
  },
  seinosuke: {
    name: '誠之介（若侍）',
    color: '#1565C0',
    image: '/characters/seinosuke.png',
    role: '若侍',
  },
} as const

/**
 * CharacterAvatarのprops型
 */
interface CharacterAvatarProps {
  /** キャラクター種別 */
  character: CharacterType
  /** サイズ（デフォルト: 48px） */
  size?: 'sm' | 'md' | 'lg'
  /** 名前を表示するか */
  showName?: boolean
  /** カスタムクラス名 */
  className?: string
}

/**
 * サイズの定義
 */
const SIZES = {
  sm: 'h-8 w-8',
  md: 'h-12 w-12',
  lg: 'h-16 w-16',
} as const

/**
 * キャラクターアバターコンポーネント
 * @description 空兵衛と誠之介のアバターを表示
 * @example
 * <CharacterAvatar character="sorabei" showName />
 * <CharacterAvatar character="seinosuke" size="lg" />
 */
export function CharacterAvatar({
  character,
  size = 'md',
  showName = false,
  className,
}: CharacterAvatarProps) {
  const info = CHARACTER_INFO[character]

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* アバター画像 */}
      <div className={cn('rounded-full overflow-hidden bg-gray-200 flex-shrink-0', SIZES[size])}>
        <img
          src={info.image}
          alt={info.name}
          className="h-full w-full object-cover"
          onError={(e) => {
            // 画像が読み込めない場合のフォールバック
            const target = e.target as HTMLImageElement
            target.style.display = 'none'
            target.parentElement!.innerHTML = `<div class="h-full w-full flex items-center justify-center text-white font-bold" style="background-color: ${info.color}">${info.name[0]}</div>`
          }}
        />
      </div>

      {/* キャラクター名 */}
      {showName && (
        <span className="text-[13px] font-semibold" style={{ color: info.color }}>
          {info.name}
        </span>
      )}
    </div>
  )
}

/**
 * キャラクター名のみを表示するコンポーネント
 */
export function CharacterName({
  character,
  className,
}: {
  character: CharacterType
  className?: string
}) {
  const info = CHARACTER_INFO[character]

  return (
    <span className={cn('text-[13px] font-semibold', className)} style={{ color: info.color }}>
      {info.name}
    </span>
  )
}
